import { useState } from 'react';
import { useEnrollQueue } from './EnrollQueueContext';
import type { RequestDetail as RequestDetailPayload, RequestEntry } from '@shared/flow/requests';

/**
 * Logic shared between V1's `RequestDetail.tsx` and V2's
 * `shell-next/rail/ActionsDetail.tsx` — extracted verbatim (cleanup item e,
 * rail-glaze-parity QA round) so the two restyled components stop
 * maintaining two independent copies of the same mutation flow and the same
 * overlay-derivation logic. Each component keeps its OWN JSX/styling (V1
 * raw buttons, V2 Glaze `Button`/`Module`) — only the state/handlers and the
 * pure derivation move here, following `useCustomInviteForm.ts`'s precedent
 * (move the whole data layer out verbatim, leave the JSX behind).
 *
 * Deliberately NOT in `request-format.tsx`: that file's own doc comment
 * scopes it to "small formatting/presentation helpers" shared between
 * `RequestsTab`'s rows and the detail header's status/kind badges — a
 * stateful mutation hook is a different kind of thing and doesn't belong
 * there.
 */

/**
 * The queue-side affordances for a play row — "Re-enroll (adds to your
 * queue)" when the row has already been submitted, or "Open queue" when it
 * hasn't. Ported verbatim (logic, wire shape) from `RequestDetail.tsx`'s
 * original `EnrollmentActions` — see that component's (and
 * `ActionsDetail.tsx`'s) own JSX for why Re-enroll STAGES a new queue item
 * rather than mutating/resending the submitted row.
 *
 * `isSubmitted: false` means the row is still sitting in the rep's own
 * queue — the only control that case needs is `onOpenQueue`; `canReenroll`
 * and the pending/error state are irrelevant (both components should ignore
 * them whenever `isSubmitted` is false).
 *
 * `isSubmitted: true` but `canReenroll: false` means the submitted
 * enrollment is missing one of the three refs (`contactId`/`playTypeId`/
 * `playInstanceRef`) Re-enroll needs to reproduce it — the caller renders
 * nothing in that case (matches both components' original `return null`).
 */
export interface UseReenrollResult {
  isSubmitted: boolean;
  canReenroll: boolean;
  pending: boolean;
  error: string | null;
  readOnly: boolean;
  /** No-op when `!canReenroll`. */
  onReenrollClick: () => void;
  onOpenQueue: () => void;
}

export function useReenroll(
  enrollment: NonNullable<RequestDetailPayload['enrollment']>,
  routeId: string,
): UseReenrollResult {
  const { addContact, openQueueTab, readOnly } = useEnrollQueue();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const isSubmitted = enrollment.status === 'submitted';

  // Re-enroll needs all three refs to reproduce the same enrollment; without
  // them the button could only stage an UNASSIGNED item for some other play,
  // which is not what "re-enroll" says.
  const { contactId, playTypeId, playInstanceRef } = enrollment;
  const canReenroll = isSubmitted && !!contactId && playTypeId !== null && playInstanceRef !== null;

  const onReenrollClick = () => {
    if (!canReenroll || !contactId || playTypeId === null || playInstanceRef === null) return;
    setError(null);
    setPending(true);
    void (async () => {
      try {
        await addContact(contactId, {
          playTypeId,
          playInstanceRef,
          sourceContext: `Re-enrolled from request ${routeId}`,
        });
        setPending(false);
        openQueueTab();
      } catch (err) {
        // The server's own `problem()` title/detail, surfaced verbatim — a
        // rep never sees a bare status code (CLAUDE.md).
        setError(err instanceof Error ? err.message : 'Could not add this contact to your queue.');
        setPending(false);
      }
    })();
  };

  return { isSubmitted, canReenroll, pending, error, readOnly, onReenrollClick, onOpenQueue: openQueueTab };
}

/** "Applied to contact" derivation's result — `null` when the entry carries no `contact_field_overlay` binding. */
export interface AppliedOverlayInfo {
  field: string;
  displayValue: string;
  /** Whether the entry carries a resolvable contact id/email to jump to — a contact-less run (e.g. an account-scoped action) still shows the staged value with no jump control. */
  canJump: boolean;
}

/**
 * Only non-`null` when the completed run's `appliedSnapshot` carries a
 * `contact_field_overlay` binding. Ported verbatim (logic, narrowing) from
 * `RequestDetail.tsx`'s original `AppliedToContact`.
 */
export function appliedOverlayFor(entry: RequestEntry): AppliedOverlayInfo | null {
  const overlay = entry.appliedSnapshot?.find(b => b.destination.type === 'contact_field_overlay');
  if (!overlay || overlay.destination.type !== 'contact_field_overlay') return null;
  const canJump = !!(entry.contact?.id || entry.contact?.email);
  const displayValue = typeof overlay.value === 'string' || typeof overlay.value === 'number' || typeof overlay.value === 'boolean'
    ? String(overlay.value)
    : JSON.stringify(overlay.value);
  return { field: overlay.destination.field, displayValue, canJump };
}
